import { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, CheckSquare, ChevronDown, ChevronUp, ChevronsUpDown, Circle, ClipboardList, Edit2, ExternalLink, Flag, History, ListTodo, Loader2, Paperclip, Plus, Save, StickyNote, Tag, Trash2, Users, X } from 'lucide-react';
import { Modal, Input, Select, Textarea, Avatar, InfoBlock, confirmDialog, ClientSearchSelect, NoteEditor, NoteFormatButtons, RichNote , RichNoteBox} from './ui.jsx';
import { canDelete } from '../seatPermissions.js';
import { searchTokens, matchesAllTokens, clientSearchHaystack } from '../searchNorm.js';
import { columnasTareas, comparadorDeTareas, siguienteOrden } from '../taskSort.js';
import { toast } from './Toast.jsx';
import PapeleraModal from './PapeleraModal.jsx';
// Que se puede adjuntar y como se pinta: las MISMAS piezas que las notas de
// poliza. Asniel Lopez (Pomares Insurance Group, 2026-09-11) pidio PDF y audio
// en Tareas; el servidor ya los aceptaba —guarda { name, type, size, blobUrl }
// sin mirar el tipo—, lo que no dejaba era esta pantalla.
import { ACEPTA_NOTA, archivoAdmitido, motivoRechazo } from '../adjuntosNota.js';
import { MiniaturaAdjunto, AdjuntoGuardado } from './Adjunto.jsx';
import {
  createTask as apiCreateTask,
  updateTask as apiUpdateTask,
  deleteTask as apiDeleteTask,
  listDeletedTasks,
  restoreTask,
  getAgencySettings as apiGetAgencySettings,
  updateAgencySettings as apiUpdateAgencySettings,
  listEntityHistory,
  listTaskNotes,
  addTaskNote,
  editTaskNote,
  deleteTaskNote,
  getTasksActivitySummary as apiGetTasksActivitySummary,
  uploadNoteAttachment,
} from '../api.js';

const uid = () => Math.random().toString(36).substr(2, 9);

// Task type catalogue (stored in the DB `category` field by id). Shared by the
// form selector, the table "Tipo" column and the type filter. Each carries a
// colour so the pill reads at a glance.
export const TASK_TYPES = [
  { id: 'llamada',     es: 'Llamada',     en: 'Call',      color: '#3b82f6' },
  { id: 'seguimiento', es: 'Seguimiento', en: 'Follow-up', color: '#8b5cf6' },
  { id: 'renovacion',  es: 'Renovación',  en: 'Renewal',   color: '#10b981' },
  { id: 'documento',   es: 'Documento',   en: 'Document',  color: '#f59e0b' },
  { id: 'cotizacion',  es: 'Cotización',  en: 'Quote',     color: '#06b6d4' },
  { id: 'reclamo',     es: 'Reclamo',     en: 'Claim',     color: '#ef4444' },
  { id: 'pago',        es: 'Pago',        en: 'Payment',   color: '#22c55e' },
  { id: 'otro',        es: 'Otro',        en: 'Other',     color: '#64748b' },
];
// Legacy free-text categories ("follow-up", "renewal"…) created before the
// catalogue existed still map to a pill instead of rendering raw.
const TYPE_ALIASES = { 'follow-up': 'seguimiento', followup: 'seguimiento', renewal: 'renovacion', call: 'llamada', document: 'documento', quote: 'cotizacion', claim: 'reclamo', payment: 'pago', other: 'otro' };
function typeMeta(category) {
  if (!category) return null;
  const id = TYPE_ALIASES[category] || category;
  return TASK_TYPES.find((t) => t.id === id) || null;
}

// Agency-defined custom task statuses [{id,label,color}], additive to the
// built-in Todo / In Progress / Done pills. Shared across all seats; loaded
// once and cached at module scope so the client-profile inline list and the
// main Tasks view stay in sync without refetching.
let _taskStatusCache = null;
function useAgencyTaskStatuses() {
  const [custom, setCustom] = useState(_taskStatusCache || []);
  useEffect(() => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    let alive = true;
    apiGetAgencySettings()
      .then((s) => {
        if (!alive) return;
        const list = Array.isArray(s?.taskStatuses) ? s.taskStatuses : [];
        _taskStatusCache = list;
        setCustom(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const persist = async (list) => {
    _taskStatusCache = list;
    setCustom(list);
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (isApi) { try { await apiUpdateAgencySettings({ taskStatuses: list }); } catch (e) { toast.error('No se pudo guardar el status: ' + e.message); } }
  };
  return [custom, persist];
}
// Agency-defined custom task TYPES [{id,label,color}], additive to the
// built-in insurance types. Same shape/lifecycle as the custom statuses above,
// stored under AgencySetting.taskTypes; a task references one by id in `category`.
let _taskTypeCache = null;
export function useAgencyTaskTypes() {
  const [custom, setCustom] = useState(_taskTypeCache || []);
  useEffect(() => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    let alive = true;
    apiGetAgencySettings()
      .then((s) => {
        if (!alive) return;
        const list = Array.isArray(s?.taskTypes) ? s.taskTypes : [];
        _taskTypeCache = list;
        setCustom(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const persist = async (list) => {
    _taskTypeCache = list;
    setCustom(list);
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (isApi) { try { await apiUpdateAgencySettings({ taskTypes: list }); } catch (e) { toast.error('No se pudo guardar el tipo: ' + e.message); } }
  };
  return [custom, persist];
}
// Always en-US. Pin date-only ('YYYY-MM-DD') to LOCAL midnight; parse full ISO
// timestamps as-is so appending 'T00:00:00' never yields "Invalid Date".
const fmt = (d) => {
  if (!d) return '--';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  return isNaN(dt.getTime()) ? '--' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// Whole-calendar-day diff in LOCAL time. A date-only string parses as UTC
// midnight, so the old `new Date(d) - new Date()` was off by the UTC offset
// (Florida = -4/-5h) and could mark a task "overdue"/"due today" a day early
// or late when viewed in the evening (feedback_local_day_not_utc).
const daysUntil = (d) => {
  if (!d) return null;
  const target = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86400000);
};
// Today as 'YYYY-MM-DD' in LOCAL time. toISOString().split('T')[0] is UTC, so
// a task created in the Florida evening (UTC-4/-5) would record tomorrow's date
// (feedback_local_day_not_utc).
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const PRIORITY_COLORS2 = { high: { bg: 'bg-red-500/15', text: 'text-red-400' }, medium: { bg: 'bg-amber-500/15', text: 'text-amber-400' }, low: { bg: 'bg-slate-500/15', text: 'text-gray-500' } };

// Resolve a category id to {label,color}, checking the agency's custom types
// first, then the built-in catalogue (+ legacy aliases).
function resolveTypeMeta(category, customTypes, es) {
  if (!category) return null;
  const custom = (customTypes || []).find((t) => t && t.id === category);
  if (custom) return { label: custom.label, color: custom.color || '#64748b' };
  const m = typeMeta(category);
  if (m) return { label: es ? m.es : m.en, color: m.color };
  return { label: category, color: '#64748b' };
}
// ── Shared pill renderers (table cells + detail panel) ────────────────────
function TypePill({ category, es, customTypes }) {
  if (!category) return <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
  const { label, color } = resolveTypeMeta(category, customTypes, es);
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, color }}><span style={{ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 }} />{label}</span>;
}
function PriorityPill({ priority, es }) {
  const map = {
    high:   { es: 'Alta',  en: 'High',   c: 'var(--c-red,#ff3d6b)',   bg: 'var(--c-red-dim)' },
    medium: { es: 'Media', en: 'Medium', c: 'var(--c-amber,#ffb700)', bg: 'var(--c-amber-dim)' },
    low:    { es: 'Baja',  en: 'Low',    c: 'var(--c-text2,#7aa3c0)', bg: 'var(--ci-bg-05)' },
  };
  const p = map[priority] || map.medium;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '.12rem .5rem', borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: p.bg, color: p.c }}><Flag size={9} />{es ? p.es : p.en}</span>;
}
function statusMeta(task, customStatuses, es) {
  const custom = (customStatuses || []).find((s) => s.id === task.status);
  if (custom) return { label: custom.label, color: custom.color || '#64748b' };
  if (task.status === 'done') return { label: es ? 'Completada' : 'Done', color: '#10b981' };
  if (task.status === 'inprogress') return { label: es ? 'En progreso' : 'In progress', color: '#f59e0b' };
  return { label: es ? 'Pendiente' : 'To do', color: '#3b82f6' };
}
function StatusBadge({ task, customStatuses, es }) {
  const { label, color } = statusMeta(task, customStatuses, es);
  return <span style={{ display: 'inline-block', padding: '.12rem .5rem', borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}55`, whiteSpace: 'nowrap' }}>{label}</span>;
}
// Explicit 3-state + custom status switcher, used inside the detail panel.
function StatusPillRow({ task, customStatuses, onSetStatus, es = true }) {
  // These three were hardcoded in English, so a Spanish user saw "Todo /
  // In Progress / Done" in the middle of an otherwise translated screen
  // (Lissette 2026-07-22). The custom statuses below carry the agency's own
  // label and are never translated — that's correct, they're user text.
  const built = [
    { id: 'todo', label: es ? 'Pendiente' : 'Todo', col: '#3b82f6' },
    { id: 'inprogress', label: es ? 'En progreso' : 'In Progress', col: '#f59e0b' },
    { id: 'done', label: es ? 'Completada' : 'Done', col: '#10b981' },
  ];
  const all = [...built, ...(customStatuses || []).filter((s) => s && s.id && s.label).map((s) => ({ id: s.id, label: s.label, col: s.color || '#64748b' }))];
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {all.map((s) => {
      const on = task.status === s.id;
      return <button key={s.id} type="button" onClick={() => onSetStatus(task, s.id)}
        style={{ padding: '3px 11px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .12s', background: on ? s.col : 'transparent', color: on ? '#0a1628' : 'var(--c-text2,#7aa3c0)', border: `1px solid ${on ? s.col : 'var(--c-border2,#1f3f60)'}` }}>{s.label}</button>;
    })}
  </div>;
}

export function TaskCard({ task, users, clients, onEdit, onDelete, onToggle, onSetStatus, customStatuses=[], t }) {
  // Same language probe the other components in this file use — the card only
  // receives `t`, not `lang`.
  const es=(t?.common?.cancel||'').toLowerCase().startsWith('cancelar');
  const assignee=users.find(u=>u.id===task.assignedTo);
  const client=clients.find(c=>c.id===task.clientId);
  const d=daysUntil(task.dueDate);
  const due=d!==null?(d<0?{label:t.tasks.overdue,c:"text-red-400"}:d===0?{label:t.tasks.dueToday,c:"text-amber-400"}:d<=7?{label:`${d}d`,c:"text-amber-300"}:null):null;
  const pc=PRIORITY_COLORS2[task.priority]||PRIORITY_COLORS2.medium;
  const isDone = task.status==="done";
  const isInProgress = task.status==="inprogress";
  // The task may be in an agency-custom status (task.status holds its id).
  const activeCustom = customStatuses.find(s=>s.id===task.status) || null;
  const borderClass = activeCustom ? "" : isDone ? "border-l-emerald-400" : isInProgress ? "border-l-amber-400" : "border-l-blue-300";
  // Circle on the left is a status INDICATOR only. Lissette feedback
  // 2026-05-19: clicking the circle skipping the explicit pills felt wrong
  // (made tasks jump to Done unexpectedly). Status changes only via pills.
  const StatusIcon = activeCustom
    ? <Circle size={18} strokeWidth={2.5} style={{color:activeCustom.color||"#64748b"}}/>
    : isDone
    ? <CheckSquare size={18} className="text-emerald-400"/>
    : isInProgress
      ? <Circle size={18} className="text-amber-400" strokeWidth={2.5}/>
      : <Circle size={18} className="text-gray-400"/>;
  return <div className={`bg-white border border-l-4 ${borderClass} rounded-2xl p-4 ${isDone?"opacity-60":"hover:border-blue-300"}`} style={activeCustom?{borderLeftColor:activeCustom.color||"#64748b"}:undefined}>
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0" title={activeCustom?activeCustom.label:isDone?"Done":isInProgress?"In Progress":"Todo"}>{StatusIcon}</div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${isDone?"line-through text-gray-400":"text-gray-900"}`}>{task.title}</div>
        {task.description&&<div className="text-xs text-gray-400 mt-0.5 truncate">{task.description}</div>}
        {task.notes&&<div className="text-xs text-amber-600 mt-0.5 flex items-start gap-1"><StickyNote size={11} className="mt-0.5 shrink-0"/><span className="line-clamp-2 break-words">{task.notes}</span></div>}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${pc.bg} ${pc.text}`}><Flag size={9}/>{t.tasks[task.priority]}</span>
          {task.dueDate&&<span className={`text-[10px] flex items-center gap-1 ${due?due.c:"text-gray-400"}`}><Calendar size={9}/>{due?due.label:fmt(task.dueDate)}</span>}
          {assignee&&<span className="text-[10px] text-gray-500 flex items-center gap-1"><div className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-gray-900 text-[8px] font-bold" style={{backgroundColor:assignee.color}}>{assignee.initials[0]}</div>{assignee.name.split(" ")[0]}</span>}
          {client&&<span className="text-[10px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">{client.firstName} {client.lastName}</span>}
        </div>
        {/* 3-state quick-action row — explicit Todo / In Progress / Done.
            High-contrast styling on the dark theme: clear ON state with a
            colored fill, clear OFF state with a visible outline + label. */}
        {onSetStatus && <div className="flex flex-wrap gap-1 mt-2">
          {[
            { id: "todo",       label: es?"Pendiente":"Todo",       on: { background:"#3b82f6", color:"#fff", border:"1px solid #3b82f6" },        off: { background:"transparent", color:"#9ca3af", border:"1px solid #475569" } },
            { id: "inprogress", label: es?"En progreso":"In Progress", on: { background:"#f59e0b", color:"#1c1917", border:"1px solid #f59e0b" },  off: { background:"transparent", color:"#9ca3af", border:"1px solid #475569" } },
            { id: "done",       label: es?"Completada":"Done",      on: { background:"#10b981", color:"#052e1a", border:"1px solid #10b981" },     off: { background:"transparent", color:"#9ca3af", border:"1px solid #475569" } },
            ...customStatuses.filter(s=>s&&s.id&&s.label).map(s=>{ const col=s.color||"#64748b"; return { id:s.id, label:s.label, on:{ background:col, color:"#fff", border:`1px solid ${col}` }, off:{ background:"transparent", color:"#9ca3af", border:`1px solid ${col}` } }; }),
          ].map(s => (
            <button key={s.id} type="button" onClick={() => onSetStatus(task, s.id)}
              style={{
                padding: "2px 10px",
                borderRadius: 6,
                fontSize: "11px",
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "all .12s",
                ...(task.status === s.id ? s.on : s.off),
              }}>
              {s.label}
            </button>
          ))}
        </div>}
      </div>
      <div className="flex gap-1 shrink-0">
        {!isDone&&<button onClick={()=>onEdit(task)} className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-900 rounded-lg"><Edit2 size={13}/></button>}
        <button onClick={()=>onDelete(task.id)} className="p-1.5 hover:bg-red-500/10 text-gray-400 hover:text-red-400 rounded-lg"><Trash2 size={13}/></button>
      </div>
    </div>
  </div>;
}
export function TaskForm({ task, users, clients, currentUser, onSave, onClose, t, customTypes=[] }) {
  const tt=t.tasks;
  const es=(t?.common?.cancel||'').toLowerCase().startsWith('cancelar');
  const [form,setForm]=useState(task||{title:"",description:"",notes:"",category:"",priority:"medium",status:"todo",dueDate:"",assignedTo:currentUser?.id||null,clientId:null,attachments:[]});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  // Pasted/attached images (Lissette 2026-07-16, foto 5). Pasting ANYWHERE in
  // the form catches image clipboard data; files also attach via the 📷 button.
  // Bytes go straight to Blob with the notes attachment-sas flow; the task
  // stores only [{name,type,size,blobUrl}] refs.
  const [uploadingImg,setUploadingImg]=useState(false);
  const fileRef=useRef(null);
  // Se admite lo MISMO que en una nota de poliza: foto, PDF y audio. Lo que no
  // se admite se dice por su nombre y con el formato delante (motivoRechazo),
  // en vez de desaparecer en silencio: un archivo que se elige y no aparece se
  // lee como que la pantalla no funciona.
  const addImages=async(files)=>{
    const buenos=[...files].filter(f=>archivoAdmitido(f));
    const malos=[...files].filter(f=>f&&!archivoAdmitido(f));
    malos.forEach(f=>toast.error(motivoRechazo(f,es)));
    if(!buenos.length) return;
    setUploadingImg(true);
    try{
      for(const f of buenos){
        const ref=await uploadNoteAttachment(f);
        setForm(prev=>({...prev,attachments:[...(prev.attachments||[]),{...ref,name:f.name||"archivo",type:f.type,size:f.size,_preview:URL.createObjectURL(f)}]}));
      }
    }catch(e){ toast.error((es?"No se pudo subir el archivo: ":"Upload failed: ")+(e.message||e)); }
    finally{ setUploadingImg(false); }
  };
  const onPasteImages=(e)=>{
    // Al pegar solo llegan los archivos que el portapapeles traiga; se filtran
    // con la misma regla, no solo por "image/".
    const items=[...(e.clipboardData?.items||[])].filter(it=>it.kind==='file');
    const archivos=items.map(it=>it.getAsFile()).filter(f=>f&&archivoAdmitido(f));
    if(!archivos.length) return;
    e.preventDefault();
    addImages(archivos);
  };
  const delImage=(i)=>setForm(f=>({...f,attachments:(f.attachments||[]).filter((_,j)=>j!==i)}));
  return <form onSubmit={e=>{e.preventDefault();onSave(form);}} onPaste={onPasteImages} className="space-y-4">
    <Input label={tt.taskTitle} value={form.title} onChange={e=>set("title",e.target.value)} required placeholder="Call client, submit form..."/>
    <Textarea label={tt.description} value={form.description} onChange={e=>set("description",e.target.value)} placeholder="Details..."/>
    <NoteEditor label={es?"Nota":"Note"} lang={es?'es':'en'} value={form.notes||""} onChange={v=>set("notes",v)} placeholder={es?"Notas, seguimiento, resultado de llamada...":"Notes, follow-up, call outcome..."}/>
    <div>
      {/* Array.from BEFORE resetting value — clearing empties the live FileList. */}
      <input ref={fileRef} type="file" accept={ACEPTA_NOTA} multiple style={{display:"none"}} onChange={e=>{const fs=Array.from(e.target.files||[]);e.target.value="";if(fs.length)addImages(fs);}}/>
      <button type="button" onClick={()=>fileRef.current&&fileRef.current.click()} disabled={uploadingImg}
        style={{display:"inline-flex",alignItems:"center",gap:6,padding:".35rem .7rem",borderRadius:8,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".74rem",fontWeight:600,cursor:uploadingImg?"wait":"pointer",fontFamily:"inherit"}}>
        {uploadingImg?<Loader2 size={13} className="dkt-spin"/>:<Paperclip size={13}/>} {uploadingImg?(es?"Subiendo…":"Uploading…"):(es?"Adjuntar archivo — foto, PDF o audio (o pega con Ctrl+V)":"Attach file — photo, PDF or audio (or paste with Ctrl+V)")}
      </button>
      {(form.attachments||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
        {(form.attachments||[]).map((a,i)=>(
          <MiniaturaAdjunto key={i} adjunto={a} lang={es?'es':'en'} onQuitar={()=>delImage(i)}/>
        ))}
      </div>}
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Select label={es?"Tipo de tarea":"Task type"} value={form.category||""} onChange={e=>set("category",e.target.value)}>
        <option value="">{es?"— Sin tipo —":"— No type —"}</option>
        {TASK_TYPES.map(tp=><option key={tp.id} value={tp.id}>{es?tp.es:tp.en}</option>)}
        {(customTypes||[]).filter(ct=>ct&&ct.id&&ct.label).length>0&&<optgroup label={es?"Tus tipos":"Your types"}>
          {(customTypes||[]).filter(ct=>ct&&ct.id&&ct.label).map(ct=><option key={ct.id} value={ct.id}>{ct.label}</option>)}
        </optgroup>}
      </Select>
      <Select label={tt.priority} value={form.priority} onChange={e=>set("priority",e.target.value)}><option value="high">{tt.high}</option><option value="medium">{tt.medium}</option><option value="low">{tt.low}</option></Select>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Select label={tt.assignedTo} value={form.assignedTo||""} onChange={e=>set("assignedTo",e.target.value||null)}><option value="">{tt.unassigned}</option>{users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</Select>
      <Input label={tt.dueDate} type="date" value={form.dueDate} onChange={e=>set("dueDate",e.target.value)}/>
    </div>
    <ClientSearchSelect label={tt.client} clients={clients} value={form.clientId} onChange={e=>set("clientId",e.target.value||null)} lang={es?'es':'en'} noneLabel="-- optional --" />
    <div className="flex gap-3 pt-1">
      <button type="button" onClick={()=>onSave(form)} className="flex-1 bg-blue-600 hover:bg-blue-500 text-gray-900 font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"><Save size={16}/>{tt.save}</button>
      <button type="button" onClick={onClose} className="px-5 bg-gray-100 text-gray-700 font-semibold py-2.5 rounded-xl">{t.common.cancel}</button>
    </div>
  </form>;
}

// ── Table (columns) + detail panel for the main Tasks view ────────────────
const thStyle = { padding: '.5rem .65rem', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--c-border,#1a3550)', background: 'var(--ci-bg-02)', whiteSpace: 'nowrap' };
const cellStyle = { padding: '.55rem .65rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', verticalAlign: 'middle' };
const lblStyle = { fontSize: '.6rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontFamily: 'var(--font-mono)', marginBottom: 4 };

function userName(users, id, full) {
  const u = users.find((x) => x.id === id);
  if (!u) return '—';
  return full ? u.name : (u.name || '').split(' ')[0];
}

// Compact counter button for the "Notas" / "Actividad" table cells.
function CellCountButton({ icon: Icon, count, title, accent, onClick }) {
  const c = accent ? 'var(--c-amber,#ffb700)' : 'var(--c-text2,#7aa3c0)';
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', background: accent && count > 0 ? 'var(--c-amber-dim)' : 'transparent', color: c, border: `1px solid ${accent && count > 0 ? 'rgba(255,183,0,.35)' : 'var(--c-border2,#1f3f60)'}` }}>
      <Icon size={13} />{count > 0 ? count : <Plus size={11} />}
    </button>
  );
}

function TaskRow({ task, users, clients, customStatuses, customTypes, es, onClick, selectable, checked, onToggleSelect, summary, onOpenNotes, onOpenActivity, onOpenClient }) {
  const client = clients.find((c) => c.id === task.clientId);
  const first = task.firstAssignee || task.assignedTo;
  const reassigned = (task.firstAssignee && task.assignedTo && task.firstAssignee !== task.assignedTo) ? task.assignedTo : null;
  const d = daysUntil(task.dueDate);
  const dueColor = d === null ? 'var(--c-text2,#7aa3c0)' : d < 0 ? 'var(--c-red,#ff3d6b)' : d === 0 ? 'var(--c-amber,#ffb700)' : 'var(--c-text,#c8dff0)';
  const isDone = task.status === 'done';
  const dash = <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
  return (
    <tr onClick={onClick} className="dkt-task-row" style={{ borderBottom: '1px solid var(--c-border-soft,rgba(26,58,94,.5))', cursor: 'pointer', opacity: isDone ? 0.6 : 1 }}>
      {selectable && <td style={{ ...cellStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={!!checked} onChange={() => onToggleSelect(task.id)} style={{ cursor: 'pointer', width: 15, height: 15 }} />
      </td>}
      <td style={{ ...cellStyle, minWidth: 180 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {task.notes && <StickyNote size={12} style={{ color: 'var(--c-amber,#ffb700)', flexShrink: 0 }} />}
          {(task.attachments||[]).length > 0 && <span title={es ? `${task.attachments.length} adjunto(s)` : `${task.attachments.length} attachment(s)`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10.5, color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }}><Paperclip size={11} />{task.attachments.length}</span>}
          <span style={{ fontWeight: 600, textDecoration: isDone ? 'line-through' : 'none' }}>{task.title}</span>
        </div>
        {client && (onOpenClient
          ? <button type="button" onClick={(e) => { e.stopPropagation(); onOpenClient(client); }} title={es ? 'Abrir ficha del cliente' : 'Open client'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, marginTop: 2, cursor: 'pointer', fontSize: 11, color: 'var(--c-cyan,#00d4ff)', fontFamily: 'inherit' }}>
              {client.firstName} {client.lastName}<ExternalLink size={10} style={{ flexShrink: 0, opacity: .8 }} />
            </button>
          : <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{client.firstName} {client.lastName}</div>)}
      </td>
      <td style={cellStyle}><TypePill category={task.category} es={es} customTypes={customTypes} /></td>
      <td style={cellStyle}><PriorityPill priority={task.priority} es={es} /></td>
      <td style={cellStyle}><StatusBadge task={task} customStatuses={customStatuses} es={es} /></td>
      <td style={cellStyle}>{userName(users, first)}</td>
      <td style={cellStyle}>{reassigned ? userName(users, reassigned) : dash}</td>
      <td style={cellStyle}>{task.createdBy ? userName(users, task.createdBy) : dash}</td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{task.createdAt ? fmt(task.createdAt) : dash}</td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap', color: dueColor, fontWeight: d !== null && d <= 0 ? 700 : 400 }}>{task.dueDate ? fmt(task.dueDate) : dash}</td>
      {/* Notas / Actividad — direct from the row, no need to open the detail
          (Lissette 2026-07-02: "que sean columna antes de Completado por"). */}
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
        <CellCountButton icon={StickyNote} count={summary?.notes || 0} accent title={es ? 'Notas — agregar / ver' : 'Notes — add / view'} onClick={() => onOpenNotes(task)} />
      </td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
        <CellCountButton icon={History} count={summary?.events || 0} title={es ? 'Registro de actividades' : 'Activity log'} onClick={() => onOpenActivity(task)} />
      </td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{task.completedBy ? (
        <div>
          <div>{userName(users, task.completedBy)}</div>
          {task.completedAt && <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>{fmtWhen(task.completedAt)}</div>}
        </div>
      ) : dash}</td>
    </tr>
  );
}

// ── Ordenar tocando el nombre de la columna ───────────────────────
// La logica vive en ../taskSort.js (modulo plano, con pruebas propias). Aqui
// queda solo lo que es pantalla.
//
// Lissette, 2026-08-21: escogio "Creación" en el selector de fecha y la lista
// no cambio de orden. Ese selector solo decide a que fecha se aplican Desde y
// Hasta. Lo que ella queria es lo que pidio en la misma frase: "en el nombre
// de la columna poder filtros".

// Encabezado que ordena. La flecha tenue se ve SIEMPRE, no solo al pasar por
// encima: si no se ve, nadie descubre que se puede tocar.
function EncabezadoOrdenable({ col, sortBy, sortDir, onSort, es }) {
  const activo = sortBy === col.key;
  const titulo = activo
    ? (es ? 'Tocar para invertir, otra vez para quitar el orden' : 'Click to reverse, again to clear')
    : (es ? 'Ordenar por esta columna' : 'Sort by this column');
  return (
    <th style={{ ...thStyle, ...(activo ? { color: 'var(--c-cyan,#00d4ff)' } : null) }} aria-sort={activo ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(col.key)} title={titulo}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', color: 'inherit', font: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontFamily: 'inherit' }}>
        {col.label}
        {activo
          ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
          : <ChevronsUpDown size={11} style={{ opacity: .38 }} />}
      </button>
    </th>
  );
}

function TaskTable({ rows, users, clients, customStatuses, customTypes, es, onRowClick, selectable, selected, onToggleSelect, onToggleAll, actSummary, onOpenNotes, onOpenActivity, onOpenClient, sortBy, sortDir, onSort }) {
  const headers = columnasTareas(es);
  // "Select all" reflects only the currently visible rows.
  const allChecked = selectable && rows.length > 0 && rows.every((tk) => selected.has(tk.id));
  return (
    <div className="dkt-card" style={{ padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1120 }}>
        <thead><tr>
          {selectable && <th style={{ ...thStyle, width: 38, textAlign: 'center' }}>
            <input type="checkbox" checked={allChecked} onChange={(e) => onToggleAll(rows, e.target.checked)} title={es ? 'Seleccionar todo' : 'Select all'} style={{ cursor: 'pointer', width: 15, height: 15 }} />
          </th>}
          {headers.map((h) => <EncabezadoOrdenable key={h.key} col={h} sortBy={sortBy} sortDir={sortDir} onSort={onSort} es={es} />)}
        </tr></thead>
        <tbody>
          {rows.map((task) => <TaskRow key={task.id} task={task} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} es={es} onClick={() => onRowClick(task)} selectable={selectable} checked={selectable && selected.has(task.id)} onToggleSelect={onToggleSelect} summary={actSummary ? actSummary[task.id] : null} onOpenNotes={onOpenNotes} onOpenActivity={onOpenActivity} onOpenClient={onOpenClient} />)}
        </tbody>
      </table>
    </div>
  );
}

// ── Per-task activity log (who did what, when) ─────────────────────────────
// Reads the immutable ActivityLog rows scoped to this task and renders them
// beside the detail panel: every action (created, edits, status moves,
// completion) with the actor's name and the date + time it happened.
const ACTIVITY_FIELD_LABELS = {
  title: ['Título', 'Title'], description: ['Descripción', 'Description'], notes: ['Nota', 'Note'],
  dueDate: ['Fecha límite', 'Due date'], priority: ['Prioridad', 'Priority'], category: ['Tipo', 'Type'],
  assigneeId: ['Asignado a', 'Assigned to'], clientId: ['Cliente', 'Client'],
  status: ['Estado', 'Status'], customStatus: ['Estado', 'Status'],
  completedAt: ['Completado', 'Completed'], completedById: ['Completado por', 'Completed by'],
};
function activityFieldLabel(f, es) {
  const m = ACTIVITY_FIELD_LABELS[f];
  if (m) return es ? m[0] : m[1];
  return String(f || '').replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase()).trim();
}
function activityActionLabel(action, es) {
  switch (action) {
    case 'task.created': return es ? 'Creó la tarea' : 'Created the task';
    case 'task.updated': return es ? 'Actualizó la tarea' : 'Updated the task';
    case 'task.completed': return es ? 'Completó la tarea' : 'Completed the task';
    case 'task.note': return es ? 'Agregó una nota' : 'Added a note';
    case 'deleted': case 'task.deleted': return es ? 'Eliminó la tarea' : 'Deleted the task';
    default: return String(action || '');
  }
}
// Log timestamps carry the time of day — always en-US (feedback_date_format_always_en_us).
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '--' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
// Turn a task.updated `changes` diff into human lines ("Estado: Pendiente →
// Completada"). Legacy rows (written before the diff existed) only carry
// field names — those render as a plain "Campos: …" line.
function taskChangeLines(details, { users, clients, customStatuses, customTypes, es }) {
  if (!details || typeof details !== 'object') return [];
  const changes = Array.isArray(details.changes) ? details.changes : null;
  const HIDDEN = new Set(['completedAt', 'completedById', 'firstAssigneeId']);
  if (!changes) {
    if (Array.isArray(details.fields) && details.fields.length) {
      const names = details.fields.filter((f) => !HIDDEN.has(f)).map((f) => activityFieldLabel(f, es));
      return names.length ? [{ text: `${es ? 'Campos:' : 'Fields:'} ${names.join(', ')}` }] : [];
    }
    return [];
  }
  const byField = {};
  changes.forEach((ch) => { if (ch && ch.field) byField[ch.field] = ch; });
  const enumStatus = (v) => v === 'COMPLETED' ? (es ? 'Completada' : 'Done')
    : v === 'IN_PROGRESS' ? (es ? 'En progreso' : 'In progress')
    : v === 'CANCELLED' ? (es ? 'Cancelada' : 'Cancelled')
    : (es ? 'Pendiente' : 'To do');
  const customLabel = (id) => { const s = (customStatuses || []).find((x) => x && x.id === id); return s ? s.label : String(id); };
  const val = (field, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (field === 'priority') return ({ LOW: es ? 'Baja' : 'Low', MEDIUM: es ? 'Media' : 'Medium', HIGH: es ? 'Alta' : 'High', URGENT: es ? 'Urgente' : 'Urgent' })[v] || String(v);
    if (field === 'assigneeId') return userName(users, v, true);
    if (field === 'clientId') { const c = (clients || []).find((x) => x.id === v); return c ? `${c.firstName} ${c.lastName}` : '—'; }
    if (field === 'category') { const m = resolveTypeMeta(v, customTypes, es); return m ? m.label : String(v); }
    if (field === 'dueDate') return fmt(String(v).split('T')[0]);
    return String(v);
  };
  const lines = [];
  // `status` (built-in enum) and `customStatus` (agency status id) describe
  // ONE user-visible thing — merge them into a single "Estado" line, letting
  // a custom id win over the enum (mirrors mapTaskFromApi's display rule).
  if (byField.status || byField.customStatus) {
    const st = byField.status, cs = byField.customStatus;
    const disp = (enumV, customV) => customV ? customLabel(customV) : enumStatus(enumV);
    lines.push({ label: es ? 'Estado' : 'Status', from: disp(st && st.from, cs && cs.from), to: disp(st && st.to, cs && cs.to) });
  }
  for (const ch of changes) {
    if (!ch || !ch.field || ch.field === 'status' || ch.field === 'customStatus' || HIDDEN.has(ch.field)) continue;
    lines.push({ label: activityFieldLabel(ch.field, es), from: val(ch.field, ch.from), to: val(ch.field, ch.to) });
  }
  return lines;
}
// `notesOnly` renders just the note thread + composer (the table's "Notas"
// modal); default renders the full merged log. `onAdded` lets the parent bump
// its counters when a note is saved from here.
function TaskActivityLog({ taskId, users, clients, customStatuses, customTypes, currentUser, es, refreshKey, notesOnly, onAdded }) {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [err, setErr] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const noteTaRef = useRef(null);
  const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
  useEffect(() => {
    if (!isApi || !taskId) return;
    let alive = true;
    setErr(false);
    // Two sources merged into ONE thread: the role-filtered audit feed AND
    // the task's note thread (served by the tasks routes so a seat reads the
    // owner's notes too). Note rows can appear in BOTH — dedupe by id.
    // Keep the previous entries on refetch (no loading flash after a status
    // change) — only the very first load shows "Cargando…" via items === null.
    Promise.all([
      notesOnly ? Promise.resolve([]) : listEntityHistory('task', taskId, { limit: 200 }).then((r) => r.activity || []).catch(() => null),
      listTaskNotes(taskId).catch(() => null),
    ]).then(([activity, notes]) => {
      if (!alive) return;
      if (activity === null && notes === null) { setErr(true); setItems([]); return; }
      const seen = new Set();
      const merged = [...(notes || []), ...(activity || [])].filter((l) => {
        if (!l || seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      });
      merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setItems(merged);
    });
    return () => { alive = false; };
  }, [taskId, refreshKey, isApi, notesOnly]);
  // Pasted/attached images for the NEXT note (Lissette 2026-07-16): uploaded
  // to Blob via the notes attachment-sas flow; only the blobUrl refs go with
  // the note (details.images) — the backend signs them for display.
  const [noteImgs, setNoteImgs] = useState([]); // [{blobUrl,_preview}]
  const [noteImgUploading, setNoteImgUploading] = useState(false);
  const noteImgRef = useRef(null);
  const addNoteImages = async (files) => {
    const buenos = [...files].filter((f) => archivoAdmitido(f));
    [...files].filter((f) => f && !archivoAdmitido(f)).forEach((f) => toast.error(motivoRechazo(f, es)));
    if (!buenos.length) return;
    if (noteImgs.length + buenos.length > 6) { toast.error(es ? 'Máximo 6 archivos por nota.' : 'Max 6 files per note.'); return; }
    setNoteImgUploading(true);
    try {
      for (const f of buenos) {
        const ref = await uploadNoteAttachment(f);
        // El NOMBRE viaja con la vista previa: sin el, una nota recien escrita
        // enseñaria "archivo" en lugar de como se llama el PDF que se acaba de
        // elegir (la URL local `blob:` no lleva nombre ni extension).
        setNoteImgs((prev) => [...prev, { blobUrl: ref.blobUrl, name: f.name || '', _preview: URL.createObjectURL(f) }]);
      }
    } catch (e) { toast.error((es ? 'No se pudo subir el archivo: ' : 'Upload failed: ') + (e.message || e)); }
    finally { setNoteImgUploading(false); }
  };
  // Corregir o retirar una nota propia (Francisco Picado, Pomares Insurance
  // Group, 2026-08-25: "a veces uno se suele confundir y quiere editar la nota
  // para corregir o agregar informacion faltante").
  //
  // En el servidor la fila NO se toca: activity_logs es append-only por un
  // trigger de la base de datos, asi que la correccion se guarda como una fila
  // nueva y la nota queda marcada. Por eso se recarga desde el servidor en vez
  // de parchear el estado local: lo que se ve tiene que ser lo plegado, no lo
  // que este lado supone.
  const [editandoNota, setEditandoNota] = useState(null); // {id, texto}
  const [guardandoNota, setGuardandoNota] = useState(false);
  const recargarNotas = () => {
    listTaskNotes(taskId)
      .then((rows) => setItems((prev) => {
        const otros = (prev || []).filter((l) => l.action !== 'task.note');
        const merged = [...rows, ...otros];
        merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return merged;
      }))
      .catch(() => {});
  };
  const guardarEdicion = async () => {
    const texto = (editandoNota?.texto || '').trim();
    // Una nota de solo fotos se puede corregir con el texto vacio: el servidor
    // conserva sus fotos (tasks.routes.ts, pliegue de task.note.edit).
    if ((!texto && !editandoNota?.conFotos) || guardandoNota) return;
    setGuardandoNota(true);
    try {
      await editTaskNote(taskId, editandoNota.id, texto);
      setEditandoNota(null);
      recargarNotas();
    } catch (e) {
      toast.error((es ? 'No se pudo corregir la nota: ' : 'Could not edit the note: ') + (e.message || e));
    } finally { setGuardandoNota(false); }
  };
  const retirarNota = async (id) => {
    // Se avisa de que queda el rastro: es lo que hace que "eliminar" aqui no
    // signifique lo mismo que en otras pantallas, y no puede ser una sorpresa.
    if (!window.confirm(es
      ? 'La nota dejara de verse, pero quedara marcada como eliminada en el registro. ¿Continuar?'
      : 'The note will stop showing, but it stays marked as deleted in the log. Continue?')) return;
    try {
      await deleteTaskNote(taskId, id);
      recargarNotas();
    } catch (e) {
      toast.error((es ? 'No se pudo eliminar la nota: ' : 'Could not delete the note: ') + (e.message || e));
    }
  };

  const submitNote = async () => {
    const text = noteText.trim();
    if ((!text && noteImgs.length === 0) || saving) return;
    setSaving(true);
    try {
      const note = await addTaskNote(taskId, text, noteImgs.map((i) => i.blobUrl));
      setItems((prev) => [note, ...(prev || [])]);
      setNoteText('');
      setNoteImgs([]);
      if (onAdded) onAdded(note);
    } catch (e) {
      toast.error((es ? 'No se pudo guardar la nota: ' : 'Could not save the note: ') + e.message);
    } finally {
      setSaving(false);
    }
  };
  const muted = { fontSize: '.74rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' };
  const HeaderIcon = notesOnly ? StickyNote : History;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ ...lblStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 5 }}><HeaderIcon size={11} />{notesOnly ? (es ? 'Notas' : 'Notes') : (es ? 'Registro de actividades' : 'Activity log')}</div>
      {isApi && <div>
        <NoteFormatButtons taRef={noteTaRef} value={noteText} onChange={setNoteText} lang={es ? 'es' : 'en'} compact style={{ marginBottom: 5 }} />
        <RichNoteBox
          boxRef={noteTaRef}
          value={noteText}
          onChange={setNoteText}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNote(); } }}
          onPasteFiles={addNoteImages}
          placeholder={es ? 'Escribe una nota… (puedes pegar fotos o adjuntar PDF y audio)' : 'Write a note… (paste photos, or attach PDF and audio)'}
          rows={2}
          style={{ width: '100%', resize: 'vertical', fontSize: '.78rem', lineHeight: 1.45, fontFamily: 'inherit', color: 'var(--c-text,#c8dff0)', background: 'var(--ci-bg-05)', border: '1px solid var(--c-border2,#1f3f60)', borderRadius: 8, padding: '.45rem .6rem', boxSizing: 'border-box' }}
        />
        {noteImgs.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {noteImgs.map((im, i) => (
            <MiniaturaAdjunto key={i} adjunto={im} lado={52} lang={es ? 'es' : 'en'}
              onQuitar={() => setNoteImgs((prev) => prev.filter((_, j) => j !== i))} />
          ))}
        </div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
          <button type="button" onClick={submitNote} disabled={saving || (!noteText.trim() && noteImgs.length === 0)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: saving || (!noteText.trim() && noteImgs.length === 0) ? 'default' : 'pointer', background: 'var(--c-amber-dim)', color: 'var(--c-amber,#ffb700)', border: '1px solid rgba(255,183,0,.35)', opacity: saving || (!noteText.trim() && noteImgs.length === 0) ? 0.5 : 1 }}>
            <StickyNote size={12} />{saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Agregar nota' : 'Add note')}
          </button>
          <input ref={noteImgRef} type="file" accept={ACEPTA_NOTA} multiple style={{ display: 'none' }}
            onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) addNoteImages(fs); }} />
          <button type="button" onClick={() => noteImgRef.current && noteImgRef.current.click()} disabled={noteImgUploading}
            title={es ? 'Adjuntar foto, PDF o audio — también puedes pegar con Ctrl+V' : 'Attach photo, PDF or audio — you can also paste with Ctrl+V'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: noteImgUploading ? 'wait' : 'pointer', background: 'transparent', color: 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border2,#1f3f60)' }}>
            {noteImgUploading ? <Loader2 size={12} className="dkt-spin" /> : <Paperclip size={12} />}{noteImgUploading ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Adjuntar' : 'Attach')}
          </button>
        </div>
      </div>}
      {!isApi && <div style={muted}>{es ? 'Disponible al iniciar sesión.' : 'Available when signed in.'}</div>}
      {isApi && items === null && !err && <div style={muted}>{es ? 'Cargando…' : 'Loading…'}</div>}
      {isApi && err && <div style={muted}>{es ? 'No se pudo cargar el registro.' : 'Could not load the log.'}</div>}
      {isApi && !err && items && items.length === 0 && <div style={muted}>{notesOnly ? (es ? 'Sin notas todavía.' : 'No notes yet.') : (es ? 'Sin actividad registrada todavía.' : 'No activity recorded yet.')}</div>}
      {isApi && items && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 440, overflowY: 'auto', paddingRight: 4 }}>
          {items.map((l) => {
            // listActivity/listTaskNotes map the author to `userName`; fall
            // back to the SPA users list for rows missing it.
            const who = l.userName || userName(users, l.userId, true);
            const isNote = l.action === 'task.note';
            const lines = l.action === 'task.updated' ? taskChangeLines(l.details, { users, clients, customStatuses, customTypes, es }) : [];
            return (
              <div key={l.id} style={{ borderLeft: `2px solid ${isNote ? 'rgba(255,183,0,.5)' : 'var(--c-border2,#1f3f60)'}`, paddingLeft: 10 }}>
                <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-text,#c8dff0)' }}>{activityActionLabel(l.action, es)}</div>
                {/* Una nota RETIRADA no desaparece del hilo: se queda en su
                    sitio marcada. Un hilo con huecos deja respuestas que ya no
                    se entienden, y ademas el registro conserva el original. */}
                {isNote && l.details && l.details.deleted && (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border2,#1f3f60)', borderRadius: 8, padding: '.4rem .6rem', marginTop: 3 }}>
                    {es ? 'Nota eliminada' : 'Note deleted'}{l.details.deletedBy ? ` · ${l.details.deletedBy}` : ''}
                  </div>
                )}
                {isNote && editandoNota && editandoNota.id === l.id ? (
                  <div style={{ marginTop: 4 }}>
                    <textarea value={editandoNota.texto} autoFocus
                      onChange={(e) => setEditandoNota((p) => ({ ...p, texto: e.target.value }))}
                      style={{ width: '100%', minHeight: 64, resize: 'vertical', fontSize: '.78rem', lineHeight: 1.5, padding: '.45rem .6rem', borderRadius: 8, border: '1px solid var(--c-border2,#1f3f60)', background: 'var(--c-bg,#0d1b2a)', color: 'var(--c-text,#c8dff0)', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button type="button" onClick={guardarEdicion} disabled={guardandoNota || (!editandoNota.texto.trim() && !editandoNota.conFotos)}
                        style={{ padding: '3px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700, border: 'none', cursor: guardandoNota ? 'wait' : 'pointer', background: 'var(--c-cyan,#00d4ff)', color: '#06283d' }}>
                        {guardandoNota ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
                      </button>
                      <button type="button" onClick={() => setEditandoNota(null)}
                        style={{ padding: '3px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border2,#1f3f60)' }}>
                        {es ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ) : isNote && l.details && l.details.note && !l.details.deleted && (
                  <div style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.5, background: 'var(--c-amber-dim)', border: '1px solid rgba(255,183,0,.2)', borderRadius: 8, padding: '.45rem .6rem', marginTop: 3, overflowWrap: 'anywhere' }}><RichNote text={String(l.details.note)} /></div>
                )}
                {/* Los adjuntos de la nota (URLs firmadas): la foto se amplia al
                    hacer clic, el audio se escucha aqui mismo y el PDF se abre
                    con su nombre delante. */}
                {isNote && l.details && !l.details.deleted && Array.isArray(l.details.images) && l.details.images.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {l.details.images.map((u, ui) => (
                      <AdjuntoGuardado key={ui} url={u} lado={84} lang={es ? 'es' : 'en'} />
                    ))}
                  </div>
                )}
                {lines.map((ln, i) => (
                  <div key={i} style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2, overflowWrap: 'anywhere' }}>
                    {ln.text ? ln.text : <>{ln.label}: <span style={{ opacity: .8 }}>{ln.from}</span> {'→'} <span style={{ fontWeight: 600 }}>{ln.to}</span></>}
                  </div>
                ))}
                <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{who} · {fmtWhen(l.createdAt)}</span>
                  {/* Que se vea que se corrigio. Sin esta marca, "editar" seria
                      reescribir la historia sin dejar constancia. */}
                  {isNote && l.details && l.details.editedAt && !l.details.deleted && (
                    <span style={{ fontStyle: 'italic' }}>· {es ? 'editada' : 'edited'} {fmtWhen(l.details.editedAt)}</span>
                  )}
                  {/* Solo el autor. Un agente no reescribe la nota de otro; el
                      servidor lo comprueba igualmente. */}
                  {isNote && isApi && !l.details?.deleted && currentUser && l.userId === currentUser.id && !editandoNota && (
                    <>
                      <button type="button" onClick={() => setEditandoNota({ id: l.id, texto: String(l.details?.note || ''), conFotos: Array.isArray(l.details?.images) && l.details.images.length > 0 })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-text2,#7aa3c0)', fontSize: '.68rem', fontWeight: 700, fontFamily: 'inherit' }}>
                        {es ? 'Editar' : 'Edit'}
                      </button>
                      <button type="button" onClick={() => retirarNota(l.id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-text2,#7aa3c0)', fontSize: '.68rem', fontWeight: 700, fontFamily: 'inherit' }}>
                        {es ? 'Eliminar' : 'Delete'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskDetail({ task, users, clients, customStatuses, customTypes, currentUser, es, onEdit, onDelete, onSetStatus, onOpenClient }) {
  const client = clients.find((c) => c.id === task.clientId);
  const first = task.firstAssignee || task.assignedTo;
  const reassigned = (task.firstAssignee && task.assignedTo && task.firstAssignee !== task.assignedTo) ? task.assignedTo : null;
  // Refetch the log whenever any visible field of the task changes (status
  // pills, edits merged back into the list) so the panel stays current.
  const refreshKey = JSON.stringify([task.status, task.assignedTo, task.dueDate, task.priority, task.title, task.notes, task.description, task.category, task.clientId]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-start' }}>
    <div style={{ flex: '1 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <TypePill category={task.category} es={es} customTypes={customTypes} />
        <PriorityPill priority={task.priority} es={es} />
        <StatusBadge task={task} customStatuses={customStatuses} es={es} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.4rem .9rem' }}>
        <InfoBlock label={es ? 'Asignado a' : 'Assigned to'} value={userName(users, first, true)} />
        <InfoBlock label={es ? 'Reasignado a' : 'Reassigned to'} value={reassigned ? userName(users, reassigned, true) : '—'} />
        <InfoBlock label={es ? 'Creado por' : 'Created by'} value={task.createdBy ? userName(users, task.createdBy, true) : '—'} />
        <InfoBlock label={es ? 'Creado' : 'Created'} value={task.createdAt ? fmt(task.createdAt) : '—'} />
        <InfoBlock label={es ? 'Fecha límite' : 'Due date'} value={task.dueDate ? fmt(task.dueDate) : '—'} />
        <InfoBlock label={es ? 'Completado por' : 'Completed by'} value={task.completedBy ? userName(users, task.completedBy, true) : '—'} />
        {task.completedAt && <InfoBlock label={es ? 'Completado' : 'Completed'} value={fmtWhen(task.completedAt)} />}
        {client && <InfoBlock label={es ? 'Cliente' : 'Client'} value={onOpenClient
          ? <button type="button" onClick={() => onOpenClient(client)} title={es ? 'Abrir ficha del cliente' : 'Open client'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '.78rem', color: 'var(--c-cyan,#00d4ff)', fontFamily: 'inherit' }}>
              {client.firstName} {client.lastName}<ExternalLink size={11} style={{ flexShrink: 0, opacity: .8 }} />
            </button>
          : `${client.firstName} ${client.lastName}`} />}
      </div>
      {task.description && <div>
        <div style={lblStyle}>{es ? 'Descripción' : 'Description'}</div>
        <div style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{task.description}</div>
      </div>}
      {/* Las imagenes adjuntas, AL ABRIR la tarea.
          Lissette 2026-09-02, queja 5: "cuando el que crea la tarea coloca una
          imagen, los demas tienen que darle editar tarea para poder verla; eso
          es muy fastidioso, deberia salir ya al abrir la tarea".
          Tenia razon y era literal: `attachments` solo se pintaba dentro de
          TaskForm. Aqui solo habia un contador en la fila de la tabla, asi que
          para VER la foto habia que entrar a EDITAR — y quien solo tiene la
          tarea asignada no deberia tener que abrir el formulario de edicion
          para mirar una imagen. Mismo tamano y mismo clic-para-ampliar que en
          el formulario; sin la X de borrar, que aqui no se edita nada. */}
      {(task.attachments || []).length > 0 && <div>
        <div style={lblStyle}>{es ? 'Adjuntos' : 'Attachments'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {(task.attachments || []).map((a, i) => (
            <AdjuntoGuardado key={i} url={a.url || a.blobUrl} nombre={a.name} lado={88} lang={es ? 'es' : 'en'} />
          ))}
        </div>
      </div>}
      <div>
        <div style={lblStyle}>{es ? 'Nota' : 'Note'}</div>
        {task.notes
          ? <div style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.55, background: 'var(--c-amber-dim)', border: '1px solid rgba(255,183,0,.2)', borderRadius: 10, padding: '.7rem .85rem' }}><RichNote text={task.notes} /></div>
          : <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' }}>{es ? 'Sin nota' : 'No note'}</div>}
      </div>
      <div>
        <div style={lblStyle}>{es ? 'Cambiar estado' : 'Change status'}</div>
        <StatusPillRow task={task} customStatuses={customStatuses} onSetStatus={onSetStatus} es={es} />
      </div>
      <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid var(--c-border,#1a3550)' }}>
        <button onClick={onEdit} className="dkt-btn-s" style={{ flex: 1 }}><Edit2 size={15} />{es ? 'Editar' : 'Edit'}</button>
        <button onClick={onDelete} className="dkt-btn-d"><Trash2 size={14} />{es ? 'Eliminar' : 'Delete'}</button>
      </div>
    </div>
    <div style={{ flex: '1 1 240px', minWidth: 230, maxWidth: 360, background: 'var(--ci-bg-02)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.75rem .85rem' }}>
      <TaskActivityLog taskId={task.id} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} currentUser={currentUser} es={es} refreshKey={refreshKey} />
    </div>
    </div>
  );
}

export function TasksView({ tasks, onTasksChange, users, clients, currentUser, t, onOpenClient }) {
  const tt=t.tasks;
  const es=(t?.common?.cancel||'').toLowerCase().startsWith('cancelar');
  const [tab,setTab]=useState("todas");
  // Papelera (2026-10-15): las tareas borradas se pueden devolver.
  const [showTrash,setShowTrash]=useState(false);
  const [searchQ,setSearchQ]=useState("");
  const [statusF,setStatusF]=useState("active");
  const [priorityF,setPriorityF]=useState("all");
  const [typeF,setTypeF]=useState("all");
  // Supervisors (owner/agent) default to "all" so they see EVERYONE's tasks on
  // open — incl. tasks they assigned to a seat (Lissette 2026-06-23: she wasn't
  // seeing a task she created for a seat because "mine" hid others' tasks).
  // Seats keep "mine" (their assignee filter is hidden anyway).
  // TODOS arrancan viendo LAS DE TODOS (Lissette 2026-09-16: *"necesito que
  // todos puedan ver las tareas de todos, no importa quien la hizo"*). Antes
  // el asiento arrancaba en "mis tareas" y ademas no podia cambiarlo.
  const [assigneeF,setAssigneeF]=useState("all");
  const [dateField,setDateField]=useState("due");
  const [dateFrom,setDateFrom]=useState("");
  const [dateTo,setDateTo]=useState("");
  // Orden por columna. "" = el orden por defecto de siempre (en progreso
  // arriba, luego prioridad). Tocar el encabezado sube, otra vez baja, y la
  // tercera devuelve el defecto: nunca se queda uno atrapado en un orden.
  const [sortBy,setSortBy]=useState("");
  const [sortDir,setSortDir]=useState("asc");
  const toggleSort=(key)=>{
    const n=siguienteOrden(sortBy,sortDir,key);
    setSortBy(n.sortBy); setSortDir(n.sortDir);
  };
  const [modal,setModal]=useState(null);
  const [detailId,setDetailId]=useState(null);
  // Multi-select for bulk delete. Only available when the user is allowed to
  // delete (owner/agent, or a seat the agency granted "records.delete").
  const allowDelete=canDelete(currentUser);
  const [selected,setSelected]=useState(()=>new Set());
  const [customStatuses,persistCustomStatuses]=useAgencyTaskStatuses();
  const [showStatusMgr,setShowStatusMgr]=useState(false);
  const [newStatusLabel,setNewStatusLabel]=useState("");
  const [newStatusColor,setNewStatusColor]=useState("#0ea5e9");
  const addCustomStatus=()=>{ const label=newStatusLabel.trim(); if(!label) return; persistCustomStatuses([...(customStatuses||[]),{id:`cs_${Date.now()}`,label,color:newStatusColor}]); setNewStatusLabel(""); setNewStatusColor("#0ea5e9"); };
  const removeCustomStatus=(id)=>persistCustomStatuses((customStatuses||[]).filter(s=>s.id!==id));
  const [customTypes,persistCustomTypes]=useAgencyTaskTypes();
  const [showTypeMgr,setShowTypeMgr]=useState(false);
  const [newTypeLabel,setNewTypeLabel]=useState("");
  const [newTypeColor,setNewTypeColor]=useState("#06b6d4");
  const addCustomType=()=>{ const label=newTypeLabel.trim(); if(!label) return; persistCustomTypes([...(customTypes||[]),{id:`tt_${Date.now()}`,label,color:newTypeColor}]); setNewTypeLabel(""); setNewTypeColor("#06b6d4"); };
  const removeCustomType=(id)=>persistCustomTypes((customTypes||[]).filter(tp=>tp.id!==id));
  // Per-task {notes, events} counters for the "Notas"/"Actividad" columns —
  // one bulk fetch, refreshed whenever the task list changes (edits, status
  // moves, SSE refreshes) so the counts stay current.
  const [actSummary,setActSummary]=useState({});
  const [notesFor,setNotesFor]=useState(null);     // task → "Notas" modal
  const [activityFor,setActivityFor]=useState(null); // task → "Actividad" modal
  const isApiMode = typeof window!=="undefined" && !!window.__deskteAuth;
  useEffect(()=>{
    if(!isApiMode) return;
    let alive=true;
    apiGetTasksActivitySummary().then(s=>{ if(alive) setActSummary(s||{}); }).catch(()=>{});
    return ()=>{ alive=false; };
  },[isApiMode,tasks]);
  // A note saved from the "Notas" modal bumps both counters in place (a note
  // is also an activity row) — no need to refetch the whole summary.
  const bumpNoteCount=(taskId)=>setActSummary(prev=>{
    const cur=prev[taskId]||{events:0,notes:0,lastAt:null};
    return {...prev,[taskId]:{...cur,notes:cur.notes+1,events:cur.events+1}};
  });
  // Sidebar click on "Tasks" closes any open form / detail.
  useEffect(()=>{
    const h=(e)=>{ if(e?.detail?.view==="tasks"){ setModal(null); setDetailId(null); setNotesFor(null); setActivityFor(null); } };
    window.addEventListener("deskte:nav-reset",h);
    return ()=>window.removeEventListener("deskte:nav-reset",h);
  },[]);
  const isOwner=currentUser?.role!=="assistant";
  // Index the book once so the per-task client lookup below isn't a linear
  // scan inside the filter (the SPA holds every client in memory).
  const clientsByIdTV=useMemo(()=>{
    const m=new Map();
    (clients||[]).forEach(c=>{ if(c&&c.id) m.set(c.id,c); });
    return m;
  },[clients]);
  const searchToks=useMemo(()=>searchTokens(searchQ),[searchQ]);
  const vis=useMemo(()=>tasks.filter(task=>{
    if(searchToks.length){
      const cl=task.clientId?clientsByIdTV.get(task.clientId):null;
      // The client's full haystack (name, spouse, dependents) OR the task's
      // own text — so "julio" finds his tasks and "renovacion" finds them by
      // title, with one box.
      const hay=[
        cl?clientSearchHaystack(cl):"",
        task.title||"", task.description||"", task.notes||"", task.category||"",
      ].join(" ");
      if(!matchesAllTokens(hay,searchToks)) return false;
    }
    // ANTES aqui se caian, sin remedio, las tareas de otra persona cuando quien
    // miraba era un asiento. El servidor lleva compartiendolas TODAS desde el
    // 2026-07-08 ("al igual que los tickets"); lo que quedaba escondiendolas era
    // esta linea, y no habia filtro que la deshiciera. Lissette, 2026-09-16:
    // *"necesito que todos puedan ver las tareas de todos, no importa quien la
    // hizo"*. Quien entra al modulo sigue decidiendolo el permiso tasks.view.
    // Status filter: "active" hides done; "all" shows everything; any other
    // value (todo/inprogress/done or a custom status id) is an exact match.
    if(statusF==="active"){ if(task.status==="done") return false; }
    else if(statusF!=="all"){ if(task.status!==statusF) return false; }
    if(priorityF!=="all"&&task.priority!==priorityF) return false;
    if(typeF==="_none"){ if(task.category) return false; }
    else if(typeF!=="all"){ if((task.category||"")!==typeF) return false; }
    // El desplegable "Responsable" ahora lo tiene TODO el mundo: "Mis tareas" es
    // una vista que se elige, no una jaula en la que te meten.
    if(assigneeF==="mine"&&task.assignedTo!==currentUser?.id&&task.assignedTo!==null) return false;
    if(assigneeF!=="mine"&&assigneeF!=="all"&&task.assignedTo!==assigneeF) return false;
    // Date range (YYYY-MM-DD strings compare lexicographically). When a range
    // is set, tasks with no date in the chosen field fall outside it.
    if(dateFrom||dateTo){
      const dv=dateField==="created"?task.createdAt:task.dueDate;
      if(dateFrom&&(!dv||dv<dateFrom)) return false;
      if(dateTo&&(!dv||dv>dateTo)) return false;
    }
    return true;
  }).sort(comparadorDeTareas(sortBy,sortDir,{
    users, actSummary, customStatuses, es,
    // Quien sabe resolver un tipo personalizado de la agencia es esta vista,
    // no el modulo de orden: se lo pasa resuelto.
    typeLabel:(c)=>(resolveTypeMeta(c,customTypes,es)||{}).label||'',
  })),[tasks,searchToks,clientsByIdTV,statusF,priorityF,typeF,assigneeF,dateField,dateFrom,dateTo,currentUser,isOwner,sortBy,sortDir,users,actSummary,customTypes,customStatuses,es]);
  const byClient=useMemo(()=>{ if(tab!=="porcliente") return []; const m={}; vis.forEach(tk=>{ const k=tk.clientId||"none"; if(!m[k]) m[k]=[]; m[k].push(tk); }); return Object.entries(m).map(([cId,tks])=>({client:clients.find(c=>c.id===cId),tasks:tks})); },[vis,tab,clients]);
  const isApiModeTV = typeof window !== "undefined" && !!window.__deskteAuth;
  const detailTask = detailId ? tasks.find(t=>t.id===detailId) : null;
  // El "Responsable" cuenta como filtro y se limpia para TODO el mundo: ahora
  // todos lo tienen, y un filtro que no se puede limpiar esconde tareas sin
  // decir por que.
  const filtersActive = !!searchQ.trim()||statusF!=="active"||priorityF!=="all"||typeF!=="all"||!!dateFrom||!!dateTo||!!sortBy||assigneeF!=="all";
  const clearFilters=()=>{ setSearchQ(""); setStatusF("active"); setPriorityF("all"); setTypeF("all"); setDateFrom(""); setDateTo(""); setSortBy(""); setSortDir("asc"); setAssigneeF("all"); };

  const saveTask=async form=>{
    if(modal==="add"){
      if(isApiModeTV){
        try{
          const created=await apiCreateTask(form);
          const merged={...form,...created};
          // Dedup vs the SSE task.created echo, which can land before this
          // 201 resolves (feedback_sse_echo_dedup_optimistic_add).
          onTasksChange(prev=> prev.some(x=>x.id===merged.id)
            ? prev.map(x=>x.id===merged.id?{...x,...merged}:x)
            : [...prev,merged]);
        }
        catch(err){ toast.error("Failed to save task: "+err.message); return; }
      } else {
        onTasksChange(prev=>[...prev,{...form,id:uid(),createdBy:currentUser?.id,firstAssignee:form.assignedTo||currentUser?.id,createdAt:todayLocal()}]);
      }
    } else {
      let serverTask=null;
      if(isApiModeTV){
        try{ serverTask=await apiUpdateTask(modal.id,{...modal,...form}); }
        catch(err){ toast.error("Failed to update task: "+err.message); return; }
      }
      // Merge the server's authoritative copy so server-managed fields
      // (firstAssignee on reassignment, completedBy/At) land immediately.
      onTasksChange(prev=>prev.map(tk=>tk.id===modal.id?{...modal,...form,...(serverTask||{})}:tk));
    }
    setModal(null);
  };
  const setStatusFn = async (task, newStatus) => {
    if (task.status === newStatus) return;
    let serverTask=null;
    if(isApiModeTV){
      try{ serverTask=await apiUpdateTask(task.id,{...task,status:newStatus}); }
      catch(err){ toast.error("Failed to update task: "+err.message); return; }
    }
    onTasksChange(prev=>prev.map(tk=>tk.id!==task.id?tk:{...tk,status:newStatus,...(serverTask||{})}));
  };
  const delTask=async id=>{
    if(isApiModeTV){
      try{ await apiDeleteTask(id); }
      catch(err){ toast.error("Failed to delete task: "+err.message); return; }
    }
    onTasksChange(prev=>prev.filter(tk=>tk.id!==id));
  };
  const handleDetailDelete=async()=>{
    if(!detailTask) return;
    const ok=await confirmDialog({ message: es?`¿Eliminar la tarea "${detailTask.title}"?`:`Delete task "${detailTask.title}"?`, danger:true, confirmLabel: es?'Eliminar':'Delete' });
    if(!ok) return;
    await delTask(detailTask.id);
    setDetailId(null);
  };
  // ── Multi-select helpers ──────────────────────────────────────────────
  const toggleSelect=(id)=>setSelected(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleSelectAll=(rows,checked)=>setSelected(prev=>{ const n=new Set(prev); (rows||[]).forEach(tk=>{ if(checked) n.add(tk.id); else n.delete(tk.id); }); return n; });
  const clearSelection=()=>setSelected(new Set());
  // Drop ids that are no longer visible (filter change / external removal) so
  // the count never references rows the user can't see.
  useEffect(()=>{ setSelected(prev=>{ if(prev.size===0) return prev; const visIds=new Set(vis.map(tk=>tk.id)); const n=new Set([...prev].filter(id=>visIds.has(id))); return n.size===prev.size?prev:n; }); },[vis]);
  const handleBulkDelete=async()=>{
    const ids=[...selected]; if(ids.length===0) return;
    const ok=await confirmDialog({ message: es?`¿Estás seguro de borrar ${ids.length} ${ids.length===1?'tarea':'tareas'}? Se podrán devolver desde la Papelera.`:`Are you sure you want to delete ${ids.length} ${ids.length===1?'task':'tasks'}? They can be restored from the Trash.`, danger:true, confirmLabel: es?'Eliminar':'Delete' });
    if(!ok) return;
    for(const id of ids){ await delTask(id); }
    clearSelection();
  };

  // El contador cuenta LO MISMO que se ve. Antes al asiento le contaba solo las
  // suyas mientras la lista pasaba a enseñar las de todos, y dos numeros que no
  // cuadran en la misma pantalla se leen como que uno de los dos miente.
  const active=tasks.filter(tk=>tk.status!=="done").length;
  return <div>
    <div className="flex items-center justify-between mb-5">
      <div><h2 className="text-xl font-bold text-gray-900">{tt.title}</h2>{active>0&&<div className="text-xs text-gray-500 mt-0.5">{active} {es?"activas":"active"}</div>}</div>
      <div className="flex items-center gap-2">
        {isOwner&&<button onClick={()=>setShowStatusMgr(true)} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold"><Flag size={14}/>{es?"Estados":"Statuses"}</button>}
        {isOwner&&<button onClick={()=>setShowTypeMgr(true)} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold"><Tag size={14}/>{es?"Tipos":"Types"}</button>}
        <button onClick={()=>setShowTrash(true)} title={es?"Tareas borradas que todavía se pueden devolver":"Deleted tasks that can still be restored"} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold"><Trash2 size={14}/>{es?"Papelera":"Trash"}</button>
        <button onClick={()=>setModal("add")} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-gray-900 px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16}/>{tt.newTask}</button>
      </div>
    </div>
    {showTrash&&<PapeleraModal lang={es?"es":"en"} onClose={()=>setShowTrash(false)}
      titulo={es?"Papelera de tareas":"Task trash"}
      vacio={es?"No hay tareas borradas en este período.":"No tasks deleted in this period."}
      nombreDe={r=>r.title||(es?"(sin título)":"(untitled)")}
      detalleDe={r=>[r.clientName,r.assigneeName].filter(Boolean).join(" · ")}
      listar={listDeletedTasks} devolver={restoreTask}
      onRestored={tk=>{ if(tk&&tk.id) onTasksChange(prev=>prev.some(x=>x.id===tk.id)?prev:[...prev,tk]); }}/>}
    <div className="flex gap-2 mb-4">
      {[["todas",es?"Todas":"All",ClipboardList],["porcliente",es?"Por Cliente":"By Client",Users]].map(([id,label,Icon])=><button key={id} onClick={()=>setTab(id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab===id?"bg-blue-600/10 text-blue-600 border border-blue-300":"bg-white border border-gray-200 text-gray-500 hover:text-gray-900"}`}><Icon size={14}/>{label}</button>)}
    </div>
    {/* Filter bar — search / status / priority / type / assignee / date range */}
    <div style={{display:"flex",flexWrap:"wrap",gap:".6rem",alignItems:"flex-end",marginBottom:"1.1rem"}}>
      {/* Search by CLIENT or by the task itself. Tasks had no search at all,
          so finding "that task for the Gonzalez family" meant scrolling
          (Lissette 2026-07-21). Uses the shared tokenizer, so "julio perez"
          matches "Julio Perez" regardless of accents or word order. */}
      <div style={{minWidth:220,flex:"1 1 220px"}}>
        <label className="dkt-input-lbl">{es?"Buscar":"Search"}</label>
        <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} className="dkt-inp"
          placeholder={es?"Cliente, titulo o descripcion...":"Client, title or description..."} />
      </div>
      <div style={{minWidth:150,flex:"0 1 auto"}}><Select label={es?"Estado":"Status"} value={statusF} onChange={e=>setStatusF(e.target.value)}>
        <option value="active">{es?"Activas":"Active"}</option>
        <option value="todo">{es?"Pendiente":"To do"}</option>
        <option value="inprogress">{es?"En progreso":"In progress"}</option>
        <option value="done">{es?"Completada":"Done"}</option>
        {(customStatuses||[]).filter(s=>s&&s.id&&s.label).map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
        <option value="all">{es?"Todas":"All"}</option>
      </Select></div>
      <div style={{minWidth:130,flex:"0 1 auto"}}><Select label={es?"Prioridad":"Priority"} value={priorityF} onChange={e=>setPriorityF(e.target.value)}>
        <option value="all">{es?"Todas":"All"}</option>
        <option value="high">{es?"Alta":"High"}</option>
        <option value="medium">{es?"Media":"Medium"}</option>
        <option value="low">{es?"Baja":"Low"}</option>
      </Select></div>
      <div style={{minWidth:150,flex:"0 1 auto"}}><Select label={es?"Tipo":"Type"} value={typeF} onChange={e=>setTypeF(e.target.value)}>
        <option value="all">{es?"Todos":"All"}</option>
        {TASK_TYPES.map(tp=><option key={tp.id} value={tp.id}>{es?tp.es:tp.en}</option>)}
        {(customTypes||[]).filter(ct=>ct&&ct.id&&ct.label).map(ct=><option key={ct.id} value={ct.id}>{ct.label}</option>)}
        <option value="_none">{es?"Sin tipo":"No type"}</option>
      </Select></div>
      <div style={{minWidth:150,flex:"0 1 auto"}}><Select label={es?"Responsable":"Assignee"} value={assigneeF} onChange={e=>setAssigneeF(e.target.value)}>
        <option value="mine">{es?"Mis tareas":"My tasks"}</option>
        <option value="all">{es?"Todos":"Everyone"}</option>
        {users.filter(u=>u.id!==currentUser?.id).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
      </Select></div>
      {/* Este selector NO ordena ni filtra por si solo: solo decide a que fecha
          se aplican Desde y Hasta. Se llamaba "Date field", que no lo decia, y
          Lissette escogio "Creación" esperando que la lista se ordenara
          (2026-08-21). Ordenar ahora se hace tocando el nombre de la columna. */}
      <div style={{minWidth:140,flex:"0 1 auto"}}><Select label={es?"Rango de fecha por":"Date range by"} value={dateField} onChange={e=>setDateField(e.target.value)}>
        <option value="due">{es?"Fecha límite":"Due date"}</option>
        <option value="created">{es?"Creación":"Created"}</option>
      </Select></div>
      <div style={{minWidth:140,flex:"0 1 auto"}}><Input type="date" label={es?"Desde":"From"} value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/></div>
      <div style={{minWidth:140,flex:"0 1 auto"}}><Input type="date" label={es?"Hasta":"To"} value={dateTo} onChange={e=>setDateTo(e.target.value)}/></div>
      {filtersActive&&<button onClick={clearFilters} className="dkt-btn-g" style={{height:38}}><X size={13}/>{es?"Limpiar":"Clear"}</button>}
    </div>
    {allowDelete&&selected.size>0&&<div style={{display:"flex",alignItems:"center",gap:12,marginBottom:".9rem",padding:".6rem .9rem",borderRadius:12,background:"var(--c-red-dim,rgba(255,61,107,.08))",border:"1px solid rgba(255,61,107,.25)"}}>
      <span style={{fontSize:".82rem",fontWeight:600,color:"var(--c-text,#c8dff0)"}}>{selected.size} {es?(selected.size===1?"seleccionada":"seleccionadas"):(selected.size===1?"selected":"selected")}</span>
      <button onClick={handleBulkDelete} className="dkt-btn-d" style={{marginLeft:"auto"}}><Trash2 size={14}/>{es?"Eliminar seleccionadas":"Delete selected"}</button>
      <button onClick={clearSelection} className="dkt-btn-g"><X size={13}/>{es?"Cancelar":"Cancel"}</button>
    </div>}
    {vis.length===0
      ? <div style={{textAlign:"center",padding:"3.5rem 1rem",color:"var(--c-text3,#45688a)"}}>{tt.noTasks}</div>
      : tab==="todas"
        ? <TaskTable rows={vis} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} es={es} onRowClick={tk=>setDetailId(tk.id)} selectable={allowDelete} selected={selected} onToggleSelect={toggleSelect} onToggleAll={toggleSelectAll} actSummary={actSummary} onOpenNotes={setNotesFor} onOpenActivity={setActivityFor} onOpenClient={onOpenClient} sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}/>
        : <div className="space-y-6">{byClient.map(({client,tasks:gt})=><div key={client?.id||"none"}>
            <div className="flex items-center gap-2 mb-2">{client?<><Avatar client={client} size={6}/>{onOpenClient
              ? <button type="button" onClick={()=>onOpenClient(client)} title={es?"Abrir ficha del cliente":"Open client"} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline" style={{background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit"}}>{client.firstName} {client.lastName}<ExternalLink size={12}/></button>
              : <span className="text-sm font-semibold text-gray-900">{client.firstName} {client.lastName}</span>}</>:<span className="text-sm text-gray-500">{es?"Sin cliente":"No client"}</span>}<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{gt.length}</span></div>
            <TaskTable rows={gt} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} es={es} onRowClick={tk=>setDetailId(tk.id)} selectable={allowDelete} selected={selected} onToggleSelect={toggleSelect} onToggleAll={toggleSelectAll} actSummary={actSummary} onOpenNotes={setNotesFor} onOpenActivity={setActivityFor} onOpenClient={onOpenClient} sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}/>
          </div>)}</div>}
    {modal&&<Modal title={modal==="add"?tt.addTitle:tt.editTitle} onClose={()=>setModal(null)} wide>
      <TaskForm task={modal==="add"?null:modal} users={users} clients={clients} currentUser={currentUser} onSave={saveTask} onClose={()=>setModal(null)} t={t} customTypes={customTypes}/>
    </Modal>}
    {detailTask&&<Modal title={detailTask.title} onClose={()=>setDetailId(null)} wide>
      <TaskDetail task={detailTask} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} currentUser={currentUser} es={es}
        onEdit={()=>{ setModal(detailTask); setDetailId(null); }}
        onDelete={handleDetailDelete}
        onSetStatus={setStatusFn}
        onOpenClient={onOpenClient ? (c)=>{ setDetailId(null); onOpenClient(c); } : undefined}/>
    </Modal>}
    {notesFor&&<Modal title={`${es?"Notas":"Notes"} — ${notesFor.title}`} onClose={()=>setNotesFor(null)}>
      <TaskActivityLog taskId={notesFor.id} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} currentUser={currentUser} es={es} notesOnly onAdded={()=>bumpNoteCount(notesFor.id)}/>
    </Modal>}
    {activityFor&&<Modal title={`${es?"Registro de actividades":"Activity log"} — ${activityFor.title}`} onClose={()=>setActivityFor(null)}>
      <TaskActivityLog taskId={activityFor.id} users={users} clients={clients} customStatuses={customStatuses} customTypes={customTypes} currentUser={currentUser} es={es} onAdded={()=>bumpNoteCount(activityFor.id)}/>
    </Modal>}
    {showStatusMgr&&<Modal title={es?"Estados personalizados":"Custom statuses"} onClose={()=>setShowStatusMgr(false)}>
      <p style={{fontSize:".8rem",color:"var(--c-text2,#7aa3c0)",marginBottom:12}}>{es?"Los 3 estados base (Todo, In Progress, Done) no se pueden borrar. Agrega los tuyos con color — aparecen como pastillas extra en cada tarea.":"The 3 base statuses (Todo, In Progress, Done) can't be removed. Add your own with a color — they show as extra pills on every task."}</p>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
        {[{label:"Todo",color:"#3b82f6"},{label:"In Progress",color:"#f59e0b"},{label:"Done",color:"#10b981"}].map(s=><div key={s.label} style={{display:"flex",alignItems:"center",gap:8,fontSize:".82rem",color:"var(--c-text,#c8dff0)"}}><span style={{width:10,height:10,borderRadius:99,background:s.color}}/>{s.label}<span style={{marginLeft:"auto",fontSize:".66rem",color:"var(--c-text2,#7aa3c0)"}}>{es?"base":"built-in"}</span></div>)}
        {(customStatuses||[]).map(s=><div key={s.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:".82rem",color:"var(--c-text,#c8dff0)"}}><span style={{width:10,height:10,borderRadius:99,background:s.color||"#64748b"}}/>{s.label}<button onClick={()=>removeCustomStatus(s.id)} style={{marginLeft:"auto",fontSize:".7rem",color:"var(--c-red,#ff3d6b)",background:"none",border:"none",cursor:"pointer",fontWeight:700}}>{es?"Quitar":"Remove"}</button></div>)}
        {(customStatuses||[]).length===0&&<div style={{fontSize:".76rem",color:"var(--c-text2,#7aa3c0)",fontStyle:"italic"}}>{es?"Aún no hay estados personalizados.":"No custom statuses yet."}</div>}
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input type="color" value={newStatusColor} onChange={e=>setNewStatusColor(e.target.value)} style={{width:38,height:38,padding:0,border:"1px solid var(--c-border,#1a3550)",borderRadius:8,cursor:"pointer",background:"var(--c-surface2,#112538)"}}/>
        <input value={newStatusLabel} onChange={e=>setNewStatusLabel(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addCustomStatus();}} placeholder={es?"Nombre del estado":"Status name"} style={{flex:1,fontSize:".84rem",padding:"8px 10px",borderRadius:8,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text,#c8dff0)"}}/>
        <button onClick={addCustomStatus} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 py-2 rounded-xl text-sm">{es?"Agregar":"Add"}</button>
      </div>
    </Modal>}
    {showTypeMgr&&<Modal title={es?"Tipos de tarea personalizados":"Custom task types"} onClose={()=>setShowTypeMgr(false)}>
      <p style={{fontSize:".8rem",color:"var(--c-text2,#7aa3c0)",marginBottom:12}}>{es?"Los tipos base de seguros no se pueden borrar. Agrega los tuyos con color — aparecen en el selector de tipo al crear una tarea y en el filtro.":"The built-in insurance types can't be removed. Add your own with a color — they show in the type selector when creating a task and in the filter."}</p>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
        {TASK_TYPES.map(tp=><div key={tp.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:".82rem",color:"var(--c-text,#c8dff0)"}}><span style={{width:10,height:10,borderRadius:99,background:tp.color}}/>{es?tp.es:tp.en}<span style={{marginLeft:"auto",fontSize:".66rem",color:"var(--c-text2,#7aa3c0)"}}>{es?"base":"built-in"}</span></div>)}
        {(customTypes||[]).map(tp=><div key={tp.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:".82rem",color:"var(--c-text,#c8dff0)"}}><span style={{width:10,height:10,borderRadius:99,background:tp.color||"#64748b"}}/>{tp.label}<button onClick={()=>removeCustomType(tp.id)} style={{marginLeft:"auto",fontSize:".7rem",color:"var(--c-red,#ff3d6b)",background:"none",border:"none",cursor:"pointer",fontWeight:700}}>{es?"Quitar":"Remove"}</button></div>)}
        {(customTypes||[]).length===0&&<div style={{fontSize:".76rem",color:"var(--c-text2,#7aa3c0)",fontStyle:"italic"}}>{es?"Aún no hay tipos personalizados.":"No custom types yet."}</div>}
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input type="color" value={newTypeColor} onChange={e=>setNewTypeColor(e.target.value)} style={{width:38,height:38,padding:0,border:"1px solid var(--c-border,#1a3550)",borderRadius:8,cursor:"pointer",background:"var(--c-surface2,#112538)"}}/>
        <input value={newTypeLabel} onChange={e=>setNewTypeLabel(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addCustomType();}} placeholder={es?"Nombre del tipo":"Type name"} style={{flex:1,fontSize:".84rem",padding:"8px 10px",borderRadius:8,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text,#c8dff0)"}}/>
        <button onClick={addCustomType} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 py-2 rounded-xl text-sm">{es?"Agregar":"Add"}</button>
      </div>
    </Modal>}
  </div>;
}
export function ClientTasksInline({ clientId, tasks, onTasksChange, users, clients, currentUser, t }) {
  const [modal,setModal]=useState(null);
  const [customStatuses]=useAgencyTaskStatuses();
  const [customTypes]=useAgencyTaskTypes();
  const ct=tasks.filter(tk=>tk.clientId===clientId).sort((a,b)=>{ if(a.status==="done"&&b.status!=="done") return 1; if(b.status==="done"&&a.status!=="done") return -1; return 0; });
  const isApiModeT = typeof window !== "undefined" && !!window.__deskteAuth;
  const setStatusFn = async (task, newStatus) => {
    if (task.status === newStatus) return;
    let serverTask=null;
    if(isApiModeT){
      try{ serverTask=await apiUpdateTask(task.id,{...task,status:newStatus}); }
      catch(err){ toast.error("Failed to update task: "+err.message); return; }
    }
    onTasksChange(prev=>prev.map(tk=>tk.id!==task.id?tk:{...tk,status:newStatus,...(serverTask||{})}));
  };
  // Circle is display-only; status changes happen via the pills.
  const toggle = async () => {};
  const del=async id=>{
    if(isApiModeT){
      try{ await apiDeleteTask(id); }
      catch(err){ toast.error("Failed to delete task: "+err.message); return; }
    }
    onTasksChange(prev=>prev.filter(tk=>tk.id!==id));
  };
  const save = async form => {
    if (modal === "add") {
      const payload = { ...form, clientId };
      if (isApiModeT) {
        try {
          const created = await apiCreateTask(payload);
          const merged = { ...payload, ...created, clientId };
          // Dedup vs the SSE task.created echo (feedback_sse_echo_dedup_optimistic_add).
          onTasksChange(prev => prev.some(x => x.id === merged.id)
            ? prev.map(x => x.id === merged.id ? { ...x, ...merged } : x)
            : [...prev, merged]);
        } catch (err) {
          toast.error("Failed to save task: " + err.message);
          return;
        }
      } else {
        onTasksChange(prev => [...prev, { ...payload, id: uid(), createdBy: currentUser?.id, firstAssignee: payload.assignedTo || currentUser?.id, createdAt: todayLocal() }]);
      }
    } else {
      let serverTask=null;
      if (isApiModeT) {
        try {
          serverTask=await apiUpdateTask(modal.id, { ...modal, ...form });
        } catch (err) {
          toast.error("Failed to update task: " + err.message);
          return;
        }
      }
      onTasksChange(prev => prev.map(tk => tk.id === modal.id ? { ...modal, ...form, ...(serverTask||{}) } : tk));
    }
    setModal(null);
  };
  return <div>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-bold text-gray-900 flex items-center gap-2"><ListTodo size={16} className="text-blue-600"/>{t.tasks.title}</h3>
      <button onClick={()=>setModal("add")} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/40 text-blue-600 text-xs rounded-lg"><Plus size={13}/>{t.tasks.newTask}</button>
    </div>
    {ct.length===0&&<div className="text-center py-6 text-gray-400 text-sm">{t.tasks.noTasks}</div>}
    <div className="space-y-2">{ct.map(task=><TaskCard key={task.id} task={task} users={users} clients={clients} onEdit={setModal} onDelete={del} onToggle={toggle} onSetStatus={setStatusFn} customStatuses={customStatuses} t={t}/>)}</div>
    {modal&&<Modal title={modal==="add"?t.tasks.addTitle:t.tasks.editTitle} onClose={()=>setModal(null)} wide>
      <TaskForm task={modal==="add"?null:{...modal,clientId}} users={users} clients={clients} currentUser={currentUser} onSave={save} onClose={()=>setModal(null)} t={t} customTypes={customTypes}/>
    </Modal>}
  </div>;
}
